import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';

interface UserSettings {
  dashboardLayouts?: {
    [mode: string]: any[];
  };
  [key: string]: any;
}

export const useUserSettings = () => {
  const [settings, setSettings] = useState<UserSettings>({});
  const [isLoading, setIsLoading] = useState(true);
  const { user } = useAuth();

  // Load settings from database
  const loadSettings = useCallback(async () => {
    if (!user?.id) {
      setIsLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from('user_settings')
        .select('settings')
        .eq('user_id', user.id)
        .single();

      if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
        console.error('Error loading user settings:', error);
        setSettings({});
      } else if (data) {
        setSettings(data.settings as UserSettings);
      } else {
        setSettings({});
      }
    } catch (err) {
      console.error('Error loading user settings:', err);
      setSettings({});
    } finally {
      setIsLoading(false);
    }
  }, [user?.id]);

  // Save settings to database
  const saveSettings = useCallback(async (newSettings: UserSettings) => {
    if (!user?.id) return;

    try {
      const { error } = await supabase
        .from('user_settings')
        .upsert({
          user_id: user.id,
          settings: newSettings
        }, {
          onConflict: 'user_id'
        });

      if (error) {
        console.error('Error saving user settings:', error);
      } else {
        setSettings(newSettings);
      }
    } catch (err) {
      console.error('Error saving user settings:', err);
    }
  }, [user?.id]);

  // Update multiple settings
  const updateSettings = useCallback(async (newSettings: Partial<UserSettings>) => {
    const updatedSettings = {
      ...settings,
      ...newSettings
    };
    await saveSettings(updatedSettings);
  }, [settings, saveSettings]);

  // Update specific setting
  const updateSetting = useCallback(async (key: string, value: any) => {
    const newSettings = {
      ...settings,
      [key]: value
    };
    await saveSettings(newSettings);
  }, [settings, saveSettings]);

  // Get dashboard layout for specific mode
  const getDashboardLayout = useCallback((mode: string) => {
    return settings.dashboardLayouts?.[mode] || null;
  }, [settings]);

  // Save dashboard layout for specific mode
  const saveDashboardLayout = useCallback(async (mode: string, layout: any[]) => {
    const newSettings = {
      ...settings,
      dashboardLayouts: {
        ...settings.dashboardLayouts,
        [mode]: layout
      }
    };
    await saveSettings(newSettings);
  }, [settings, saveSettings]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  return {
    settings,
    isLoading,
    updateSetting,
    updateSettings,
    getDashboardLayout,
    saveDashboardLayout,
    loadSettings
  };
};